import type {
	WorkflowItemEdge,
	WorkflowItemNode,
} from "@memorang/types/src/session/new-session";
import useWorkflowStore from "./useWorkflowStore";

// this util functions generates an svg path from a list of points
export function generatePath(points: number[][]) {
	const path = points.map(([x, y]) => `${x},${y}`).join(" L");
	return `M${path} Z`;
}

export const createWorkflowReport = (
	correctNodes: WorkflowItemNode[],
	correctEdges: WorkflowItemEdge[],
) => {
	const nodes = useWorkflowStore.getState().nodes;
	const edges = useWorkflowStore.getState().edges;

	let report = "<workflow_report>\n";

	// User's workflow
	report += "  <user_workflow>\n";
	report += "    <nodes>\n";
	for (const node of nodes) {
		report += `      <node id="${node.id}" label="${node.data.label}" />\n`;
	}
	report += "    </nodes>\n";

	report += "    <edges>\n";
	for (const edge of edges) {
		report += `      <edge source="${edge.source}" target="${edge.target}"${edge.label ? ` label="${edge.label}"` : ""} />\n`;
	}
	report += "    </edges>\n";
	report += "  </user_workflow>\n";

	// Correct workflow
	report += "  <correct_workflow>\n";
	report += "    <nodes>\n";
	for (const node of correctNodes) {
		report += `      <node id="${node.id}" label="${node.data.label}" />\n`;
	}
	report += "    </nodes>\n";

	report += "    <edges>\n";
	for (const edge of correctEdges) {
		report += `      <edge source="${edge.source}" target="${edge.target}"${edge.label ? ` label="${edge.label}"` : ""} />\n`;
	}
	report += "    </edges>\n";
	report += "  </correct_workflow>\n";

	report += "</workflow_report>";
	return report;
};

const createEdgeKey = (edge: WorkflowItemEdge): string => {
	if (edge.sourceHandle) {
		return `${edge.source}-${edge.target}-${edge.sourceHandle}`;
	}
	return `${edge.source}-${edge.target}`;
};

/**
 * Compares current graph state with correct graph and returns annotated nodes/edges
 * to highlight differences between user's answer and correct answer
 */
export const compareWorkflowGraphs = (
	correctNodes: WorkflowItemNode[],
	correctEdges: WorkflowItemEdge[],
	currentNodes: WorkflowItemNode[],
	currentEdges: WorkflowItemEdge[],
): { nodes: WorkflowItemNode[]; edges: WorkflowItemEdge[] } => {
	const currentNodesMap = new Map(currentNodes.map((node) => [node.id, node]));
	const correctNodesMap = new Map(correctNodes.map((node) => [node.id, node]));
	const currentEdgesMap = new Map(
		currentEdges.map((edge) => [createEdgeKey(edge), edge]),
	);
	const correctEdgesMap = new Map(
		correctEdges.map((edge) => [createEdgeKey(edge), edge]),
	);

	// Process nodes
	const resultNodes: WorkflowItemNode[] = [];

	// Check all correct nodes
	for (const correctNode of correctNodes) {
		const currentNode = currentNodesMap.get(correctNode.id);

		if (currentNode) {
			// Node exists - check if label matches (case-insensitive)
			const labelsMatch =
				currentNode.data.label.toLowerCase() ===
				correctNode.data.label.toLowerCase();
			resultNodes.push({
				...currentNode,
				data: {
					...currentNode.data,
					correct: labelsMatch,
				},
			});
		} else {
			// Node missing in current - add with correct: false
			resultNodes.push({
				...correctNode,
				data: {
					...correctNode.data,
					correct: false,
				},
			});
		}
	}

	// Add any extra nodes from current that don't exist in correct
	for (const currentNode of currentNodes) {
		if (!correctNodesMap.has(currentNode.id)) {
			resultNodes.push({
				...currentNode,
				data: {
					...currentNode.data,
					correct: false,
				},
			});
		}
	}

	// Process edges
	const resultEdges: WorkflowItemEdge[] = [];

	// Check all correct edges
	for (const correctEdge of correctEdges) {
		const edgeKey = createEdgeKey(correctEdge);
		const currentEdge = currentEdgesMap.get(edgeKey);

		if (currentEdge) {
			// Edge exists - check if labels match
			const labelsMatch =
				(currentEdge.label || "") === (correctEdge.label || "");
			resultEdges.push({
				...currentEdge,
				data: {
					...currentEdge.data,
					correct: labelsMatch,
				},
			});
		} else {
			// Edge missing in current - add with correct: false and animated: true
			resultEdges.push({
				...correctEdge,
				data: {
					...correctEdge.data,
					correct: false,
				},
				animated: true,
			});
		}
	}

	// Add any extra edges from current that don't exist in correct
	for (const currentEdge of currentEdges) {
		const edgeKey = createEdgeKey(currentEdge);
		if (!correctEdgesMap.has(edgeKey)) {
			resultEdges.push({
				...currentEdge,
				data: {
					...currentEdge.data,
					correct: false,
				},
				animated: true,
			});
		}
	}

	return { nodes: resultNodes, edges: resultEdges };
};
